import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { feedbackService } from '@/services/admin'
import {
  LayoutDashboard,
  Brain,
  Database,
  Target,
  Settings,
  BarChart3,
  Shield,
  Building2,
  CreditCard,
  Users,
  LogOut,
  ChevronUp,
  Home,
  MessageSquare,
  MessageSquarePlus,
  Activity,
  TrendingUp,
  CheckCircle,
  Code,
  LifeBuoy,
  ExternalLink,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const navigation = [
  { name: 'Home', href: '/app', icon: Home },
  { name: 'Dashboard', href: '/app/dashboard', icon: LayoutDashboard },
  { name: 'Chat', href: '/app/chat', icon: MessageSquare },
  { name: 'Models', href: '/app/models', icon: Brain },
  { name: 'Predictions', href: '/app/predictions', icon: Target },
  { name: 'Data Sources', href: '/app/data-sources', icon: Database },
  { name: 'Pixel SDK', href: '/app/pixel', icon: Code },
  { name: 'Insights', href: '/app/analytics', icon: BarChart3 },
  { name: 'Settings', href: '/app/settings', icon: Settings },
]

const monitoringNavigation = [
  { name: 'Performance', href: '/app/performance', icon: TrendingUp },
  { name: 'Drift Monitoring', href: '/app/drift', icon: Activity },
  { name: 'SLA Dashboard', href: '/app/sla', icon: CheckCircle },
]

const adminNavigation = [
  { name: 'Admin Overview', href: '/admin', icon: Shield },
  { name: 'Tenants', href: '/admin/tenants', icon: Building2 },
  { name: 'Plans', href: '/admin/plans', icon: CreditCard },
  { name: 'User Search', href: '/admin/users', icon: Users },
  { name: 'Feedback', href: '/admin/feedback', icon: MessageSquarePlus, hasBadge: true },
]

// Tickets live in the standalone support desk (mgosupport clone), not this app.
const SUPPORT_DESK_URL =
  import.meta.env.VITE_SUPPORT_DESK_URL || 'https://brazilnut-support.vercel.app'

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

interface SidebarProps {
  mobile?: boolean
  onNavigate?: () => void
}

export function Sidebar({ mobile: _mobile = false, onNavigate }: SidebarProps = {}) {
  const location = useLocation()
  const { user, logout } = useAuth()

  // Fetch pending feedback count for super admins
  const { data: pendingFeedbackCount } = useQuery({
    queryKey: ['feedback-pending-count'],
    queryFn: async () => {
      const response = await feedbackService.listFeedback({
        page: 1,
        page_size: 1,
        status: 'submitted',
      })
      const submittedCount = response.total

      const inReviewResponse = await feedbackService.listFeedback({
        page: 1,
        page_size: 1,
        status: 'in_review',
      })
      const inReviewCount = inReviewResponse.total

      return submittedCount + inReviewCount
    },
    enabled: !!user?.is_super_admin,
    refetchInterval: 60000, // Refetch every minute
  })

  return (
    <div className="flex h-full w-64 flex-col bg-card border-r border-border/50">
      {/* Logo - matching landing page style */}
      <div className="flex h-20 items-center px-6 border-b border-border/50">
        <Link to="/app" className="flex items-center">
          <img
            src="/logo.png"
            alt="Brazil Nut AI"
            className="h-8 w-auto brightness-0 invert"
          />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href ||
            (item.href !== '/app' && location.pathname.startsWith(item.href))

          // Add data-tour attributes to specific nav items
          const getTourAttr = () => {
            if (item.href === '/app/models') return 'nav-models'
            if (item.href === '/app/predictions') return 'nav-predictions'
            if (item.href === '/app/data-sources') return 'nav-data-sources'
            return undefined
          }

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              data-tour={getTourAttr()}
              className={cn(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-foreground/70 hover:bg-background hover:text-foreground'
              )}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </Link>
          )
        })}

        {/* Monitoring Section */}
        <div className="pt-4 pb-2">
          <div className="flex items-center gap-2 px-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium text-muted-foreground uppercase">
              Monitoring
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>
        </div>
        {monitoringNavigation.map((item) => {
          const isActive = location.pathname === item.href ||
            (item.href !== '/app' && location.pathname.startsWith(item.href))

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              className={cn(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-foreground/70 hover:bg-background hover:text-foreground'
              )}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </Link>
          )
        })}

        {/* Super Admin Navigation */}
        {user?.is_super_admin && (
          <>
            <div className="pt-4 pb-2">
              <div className="flex items-center gap-2 px-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium text-muted-foreground uppercase">
                  Super Admin
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
            {adminNavigation.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/admin' && location.pathname.startsWith(item.href))

              const showBadge = 'hasBadge' in item && item.hasBadge && pendingFeedbackCount && pendingFeedbackCount > 0

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={onNavigate}
                  className={cn(
                    'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-destructive text-destructive-foreground shadow-sm'
                      : 'text-foreground/70 hover:bg-destructive/10 hover:text-destructive'
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="flex-1">{item.name}</span>
                  {showBadge && (
                    <span className={cn(
                      'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full',
                      isActive
                        ? 'bg-destructive-foreground/20 text-destructive-foreground'
                        : 'bg-destructive text-destructive-foreground'
                    )}>
                      {pendingFeedbackCount > 99 ? '99+' : pendingFeedbackCount}
                    </span>
                  )}
                </Link>
              )
            })}
            <a
              href={SUPPORT_DESK_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavigate}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 text-foreground/70 hover:bg-destructive/10 hover:text-destructive"
            >
              <LifeBuoy className="h-5 w-5" />
              <span className="flex-1">Support Desk</span>
              <ExternalLink className="h-4 w-4 opacity-50" />
            </a>
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-border/50 p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 hover:bg-background transition-all duration-200">
              <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-medium text-accent">
                  {user ? getInitials(user.name || user.email) : 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-foreground/50 truncate capitalize">
                  {user?.tenant_plan || 'Free'} Plan
                </p>
              </div>
              <ChevronUp className="h-4 w-4 text-foreground/50 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name || 'User'}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/app/settings" className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive cursor-pointer"
              onClick={async () => {
                await logout()
                window.location.href = '/login'
              }}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
